import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Crown, Zap, Infinity as InfinityIcon, Users, Loader2, Tag, Gift } from "lucide-react";

const ICONS = { free: Zap, pro: Crown, lifetime: InfinityIcon, teacher: Users };

export default function PricingModal({ open, onOpenChange, onRequireAuth }) {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [promo, setPromo] = useState("");
  const [redeem, setRedeem] = useState("");
  const [giftMsg, setGiftMsg] = useState("");
  const [giftMode, setGiftMode] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    if (open && plans.length === 0) {
      api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    }
  }, [open, plans.length]);

  const currentPlan = user && user.plan ? user.plan : "free";

  const upgrade = async (lookupKey, gift = false) => {
    if (loadingKey) return;
    if (!user) {
      onOpenChange(false);
      onRequireAuth && onRequireAuth();
      toast.message("Please log in to upgrade");
      return;
    }
    setLoadingKey(gift ? `gift-${lookupKey}` : lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        promo_code: promo.trim() || undefined,
        gift,
        gift_message: gift ? (giftMsg.trim() || undefined) : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  const redeemGift = async () => {
    if (!user) {
      onOpenChange(false);
      onRequireAuth && onRequireAuth();
      return;
    }
    if (!redeem.trim()) return;
    try {
      const { data } = await api.post("/gift/redeem", { code: redeem.trim() });
      await refreshUser();
      toast.success(data.note ? `${data.message} — "${data.note}"` : data.message);
      setRedeem("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid gift code");
    }
  };

  const priceFor = (plan) => {
    if (plan.id === "pro" && cycle === "yearly" && plan.yearly) return plan.yearly;
    return { price: plan.price, cadence: plan.cadence, lookup_key: plan.lookup_key };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b0c10] border-white/10 max-w-6xl max-h-[92vh] overflow-y-auto scroll-thin" data-testid="pricing-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-4xl uppercase tracking-tight text-center">
            Unlock the <span className="cyan-text">full studio</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-center">
            Save, export, mix and share every groove. Cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center mt-2">
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1" data-testid="billing-cycle-toggle">
            <button
              onClick={() => setCycle("monthly")}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${cycle === "monthly" ? "bg-[#00f0ff] text-black" : "text-slate-400"}`}
              data-testid="cycle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle("yearly")}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${cycle === "yearly" ? "bg-[#00f0ff] text-black" : "text-slate-400"}`}
              data-testid="cycle-yearly"
            >
              Yearly <span className={`text-[10px] ${cycle === "yearly" ? "text-black/70" : "text-[#ffab00]"}`}>Save $24</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          {plans.map((plan) => {
            const Icon = ICONS[plan.id] || Zap;
            const isCurrent = plan.id === currentPlan;
            const highlight = plan.id === "pro";
            const p = priceFor(plan);
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-2xl border p-6 flex flex-col ${
                  highlight ? "border-[#00f0ff]/50 bg-[#00f0ff]/[0.04] glow-cyan" : "border-white/10 bg-white/[0.02]"
                }`}
              >
                {highlight && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00f0ff] text-black border-0 font-tab text-[10px] tracking-widest">
                    MOST POPULAR
                  </Badge>
                )}
                {plan.id === "teacher" && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ffab00] text-black border-0 font-tab text-[10px] tracking-widest">
                    {plan.seats} SEATS
                  </Badge>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-5 h-5 ${plan.id === "lifetime" || plan.id === "teacher" ? "amber-text" : "cyan-text"}`} />
                  <h3 className="font-heading text-2xl uppercase tracking-tight">{plan.name}</h3>
                </div>
                <div className="mb-5">
                  <span className="font-heading text-4xl font-extrabold">{p.price}</span>
                  <span className="text-slate-500 text-sm ml-1">/ {p.cadence}</span>
                  {plan.id === "pro" && cycle === "yearly" && (
                    <div className="text-[11px] text-[#ffab00] font-tab mt-1">{plan.yearly.savings} vs monthly</div>
                  )}
                </div>

                <ul className="space-y-2.5 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 mt-0.5 text-[#00f0ff] shrink-0" />
                      {f}
                    </li>
                  ))}
                  {(plan.limits || []).map((f, i) => (
                    <li key={`l${i}`} className="flex items-start gap-2 text-sm text-slate-600 line-through">
                      <span className="w-4 h-4 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  {isCurrent ? (
                    <Button disabled className="w-full bg-white/5 text-slate-400" data-testid={`plan-current-${plan.id}`}>
                      Current Plan
                    </Button>
                  ) : p.lookup_key ? (
                    <>
                      <Button
                        onClick={() => upgrade(p.lookup_key)}
                        disabled={loadingKey === p.lookup_key || currentPlan === "lifetime"}
                        className={`w-full font-bold ${
                          plan.id === "lifetime" || plan.id === "teacher"
                            ? "bg-[#ffab00] text-black hover:bg-[#ffc233]"
                            : "bg-[#00f0ff] text-black hover:bg-[#33f4ff]"
                        }`}
                        data-testid={`plan-select-${plan.id}`}
                      >
                        {loadingKey === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get {plan.name}</>}
                      </Button>
                      {plan.id === "lifetime" && !giftMode && (
                        <button
                          onClick={() => setGiftMode(true)}
                          className="w-full mt-2 text-xs text-slate-400 hover:text-[#ffab00] font-tab flex items-center justify-center gap-1"
                          data-testid="gift-lifetime-button"
                        >
                          <Gift className="w-3.5 h-3.5" /> Gift it to a friend instead
                        </button>
                      )}
                      {plan.id === "lifetime" && giftMode && (
                        <div className="mt-2 space-y-2">
                          <Input
                            value={giftMsg}
                            onChange={(e) => setGiftMsg(e.target.value)}
                            placeholder="Add a personal note (optional)"
                            className="h-9 bg-[#0b0c10] border-white/10 text-xs"
                            data-testid="gift-message-input"
                          />
                          <Button
                            onClick={() => upgrade(p.lookup_key, true)}
                            disabled={loadingKey === `gift-${p.lookup_key}`}
                            className="w-full bg-[#ffab00] text-black hover:bg-[#ffc233] font-semibold text-sm"
                            data-testid="gift-confirm-button"
                          >
                            {loadingKey === `gift-${p.lookup_key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue to gift checkout"}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <Button disabled className="w-full bg-white/5 text-slate-500">Free forever</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
          <div className="relative w-full sm:w-72">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={promo}
              onChange={(e) => setPromo(e.target.value.toUpperCase())}
              placeholder="Promo code (e.g. LAUNCH20)"
              className="h-10 pl-9 bg-[#12161f] border-white/10 font-tab text-sm uppercase"
              data-testid="promo-code-input"
            />
          </div>
          {promo && (
            <span className="text-xs text-[#00f0ff] font-tab" data-testid="promo-hint">
              Applied at checkout
            </span>
          )}
        </div>

        <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-2">
          <div className="relative w-full sm:w-72">
            <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={redeem}
              onChange={(e) => setRedeem(e.target.value.toUpperCase())}
              placeholder="Have a gift code? GIFT-XXXX"
              className="h-10 pl-9 bg-[#12161f] border-white/10 font-tab text-sm uppercase"
              data-testid="gift-redeem-input"
            />
          </div>
          <Button
            onClick={redeemGift}
            variant="outline"
            className="border-white/15 bg-white/5 h-10"
            data-testid="gift-redeem-button"
          >
            Redeem
          </Button>
        </div>

        <p className="text-center text-xs text-slate-600 mt-3 font-tab">
          Secure checkout by Stripe · Apple Pay & Google Pay supported · Test card 4242 4242 4242 4242
        </p>
      </DialogContent>
    </Dialog>
  );
}
